import React, { useEffect, useId, useRef, useState } from 'react';
import { autoUpdate, flip, FloatingPortal, offset, shift, size, useFloating } from '@floating-ui/react';
import { Info } from 'lucide-react';

interface TooltipIconProps {
  label: string;
  children: React.ReactNode;
  className?: string;
  panelClassName?: string;
}

const TooltipIcon: React.FC<TooltipIconProps> = ({
  label,
  children,
  className = '',
  panelClassName = '',
}) => {
  const tooltipId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [supportsHover, setSupportsHover] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({ padding: 16, fallbackPlacements: ['top', 'bottom'] }),
      shift({ padding: 16 }),
      size({
        padding: 16,
        apply({ availableWidth, elements }) {
          elements.floating.style.maxWidth = `${Math.max(0, availableWidth)}px`;
        },
      }),
    ],
  });

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openOnHover = () => {
    clearCloseTimer();
    setIsOpen(true);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 120);
  };

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const updateSupportsHover = () => setSupportsHover(mediaQuery.matches);

    updateSupportsHover();
    mediaQuery.addEventListener('change', updateSupportsHover);

    return () => {
      mediaQuery.removeEventListener('change', updateSupportsHover);
    };
  }, []);

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !refs.floating.current?.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, refs.floating]);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      onMouseEnter={() => {
        if (supportsHover) {
          openOnHover();
        }
      }}
      onMouseLeave={() => {
        if (supportsHover) {
          scheduleClose();
        }
      }}
    >
      <button
        ref={refs.setReference}
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? tooltipId : undefined}
        onClick={() => {
          if (supportsHover) {
            openOnHover();
          } else {
            setIsOpen((current) => !current);
          }
        }}
        onFocus={() => {
          if (supportsHover) {
            openOnHover();
          }
        }}
        onBlur={() => {
          if (supportsHover) setIsOpen(false);
        }}
        className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition-colors hover:border-primary hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        <Info className="h-4 w-4" />
      </button>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            id={tooltipId}
            role="tooltip"
            style={floatingStyles}
            onMouseEnter={() => {
              if (supportsHover) openOnHover();
            }}
            onMouseLeave={() => {
              if (supportsHover) scheduleClose();
            }}
            className={`z-[1040] w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-3 text-left text-xs leading-5 text-gray-700 shadow-lg ${panelClassName}`}
          >
            {children}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default TooltipIcon;
